import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects"
import { buildCampaignFulfillmentCacheKey } from "helpers/campaign_fulfillment_pagination_helper"

import {
  GET_RECEIPTS as GET_RECEIPTS,
  CREATE_RECEIPT,
  EXPORT_RECEIPTS,
  GET_RECEIPT_TYPES,
  CREATE_RECEIPT_TYPES
} from "./actionTypes"

import {
  getReceiptsSuccess as getReceiptsSuccess,
  getReceiptsError as getReceiptsError,
  createReceiptSuccess,
  createReceiptError,
  exportReceiptsSuccess,
  exportReceiptsError,
  getReceiptTypesSuccess,
  getReceiptTypesError,
  createReceiptTypesSuccess,
  createReceiptTypesError
} from "./actions"

import {
  genericPostRequest,
  genericGetRequest,
  genericGetRequest_2,
} from "../../helpers/sf_backend_helper"
import { AppConfiguration } from "config"
import { getCurrentBrandFromLocalStorage } from "helpers/sf_utils"
import { getCurrentUser } from "store/actions"
import { bsh_getAdminUserDetails } from "helpers/sf_browser_storage_helper"
import ToastAlert, { ToastAlertIconType } from "components/Common/ToastAlert/ToastAlert"

const inFlightReceiptRequests = new Set()

function* onGetReceipts(action) {
  try {
    let user = JSON.parse(localStorage.getItem("authUser")).user
    const queryParams = action.payload?.queryParams || {}
    const useCache = Boolean(action.payload?.useCache)
    const requestedPageNumber = parseInt(queryParams?.page_number, 10) || 1
    const cacheKey = useCache ? buildCampaignFulfillmentCacheKey(queryParams) : null
    const requestKey = `${cacheKey}:${requestedPageNumber}`
    const cachedPage = useCache
      ? yield select((state) => state.Receipts.page_cache?.[cacheKey]?.[requestedPageNumber])
      : null

    if (useCache && cachedPage) {
      return
    }

    if (useCache && inFlightReceiptRequests.has(requestKey)) {
      return
    }

    if (useCache) {
      inFlightReceiptRequests.add(requestKey)
    }

    const response = yield call(
      genericGetRequest_2,
      `${AppConfiguration.BACKEND_URL}/api/v1/receipts`,
      queryParams,
      {
        Authorization: user.token,
        'account-id': user.account_id,
        'brand-id': getCurrentBrandFromLocalStorage()?.id

      }
    )
    yield put(getReceiptsSuccess(response, {
      queryParams,
      appendResults: Boolean(action.payload?.appendResults),
      useCache,
    }))
  } catch (error) {
    yield put(getReceiptsError(error))
  } finally {
    if (action.payload?.useCache) {
      const queryParams = action.payload?.queryParams || {}
      const requestedPageNumber = parseInt(queryParams?.page_number, 10) || 1
      const cacheKey = buildCampaignFulfillmentCacheKey(queryParams)
      inFlightReceiptRequests.delete(`${cacheKey}:${requestedPageNumber}`)
    }
  }
}

function* onCreateReceipt(action) {
  try {
    let user = JSON.parse(localStorage.getItem("authUser")).user
    const response = yield call(
      genericPostRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/receipts`,
      {
        body: JSON.stringify({
          ...action.payload
        }),
        headers: {
          Authorization: user.token,
          'account-id': user.account_id,
          'Content-Type': 'application/json',
          'brand-id': action?.brandId ? action?.brandId : getCurrentBrandFromLocalStorage()?.id

        }
      })

    yield put(createReceiptSuccess(response))
    yield put(getCurrentUser())
  } catch (error) {
    yield put(createReceiptError(error))
    ToastAlert.fire({ icon: ToastAlertIconType.ERROR, title: 'Failed to add funds.' })
  }
}

function* onExportReceipts(action) {
  try {
    let user = JSON.parse(localStorage.getItem("authUser")).user
    const response = yield call(
      genericPostRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/receipts_export`,
      {
        body: JSON.stringify(action.payload?.queryParams || {}),
        headers: {
          Authorization: user.token,
          'account-id': user.account_id,
          'brand-id': getCurrentBrandFromLocalStorage()?.id,
          'Content-Type': 'application/json',
        }
      }
    )
    yield put(exportReceiptsSuccess(response, action?.filename))
    ToastAlert.fire({ icon: ToastAlertIconType.OK, title: "You will receive an email when it is ready." })
  } catch (error) {
    yield put(exportReceiptsError(error))
    ToastAlert.fire({ icon: ToastAlertIconType.ERROR, title: "Failed to start export. Please try again." })
  }
}

function* onGetReceiptTypes(action) {
  try {
    const response = yield call(
      genericGetRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/receipt_types`,
      action.payload?.queryParams,
      {}
    )
    yield put(getReceiptTypesSuccess(response))
  } catch (error) {
    yield put(getReceiptTypesError(error))
  }
}

function* onCreateReceiptTypes(action) {
  try {
    let user = bsh_getAdminUserDetails();
    const URL = `${AppConfiguration.BACKEND_URL}/api/v1/receipt_types`;
    const payload = {
      body: JSON.stringify({ ...action.payload }),
      headers: {
        Authorization: user.token,
        'Content-Type': 'application/json',
      },
    }
    const response = yield call(genericPostRequest, URL, payload)
    yield put(createReceiptTypesSuccess(response))
    ToastAlert.fire({ icon: ToastAlertIconType.OK, title: 'Receipt type created successfully.' })

  } catch (error) {
    yield put(createReceiptTypesError(error))
    ToastAlert.fire({ icon: ToastAlertIconType.ERROR, title: 'Failed to create receipt type.' })

  }
}


function* ReceiptsSaga() {
  yield takeLatest(GET_RECEIPTS, onGetReceipts)
  yield takeEvery(CREATE_RECEIPT, onCreateReceipt)
  yield takeEvery(EXPORT_RECEIPTS, onExportReceipts)
  yield takeLatest(GET_RECEIPT_TYPES, onGetReceiptTypes)
  yield takeEvery(CREATE_RECEIPT_TYPES, onCreateReceiptTypes)
}

export default ReceiptsSaga
